import {
	Box,
	Button,
	CardActions,
	CardContent,
	Divider,
	FormHelperText,
	Grid,
	IconButton,
	InputAdornment,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useController,
	useFieldArray,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField, {
	InputType,
} from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppRichTextEditor from '@/common/components/editor/AppRichTextEditor';
import {
	AddSingleRequest,
	UnitSingleDetailResDTO,
} from '@/api/DTO/exercise/meditation.interface';
import VisuallyHiddenInput from '@/common/components/UploadInput';
import { useFileUploader } from '@/hooks/useFileUploader';
import { ExerciseTagDTO, FileInfo } from '@/api/DTO/common.interface';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import {
	useAddSingleMutation,
	useUpdateSingleMutation,
} from '@/api/exercise/meditationApiSlice';
import { useGetTagListQuery } from '@/api/common';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';
import { AwardType } from '@/api/enum/common.enum';
import AddRewardItemDialog from './AddRewardItemDialog';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import React from 'react';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';

interface Props {
	type: FormType;
	details?: UnitSingleDetailResDTO;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function SingleInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [addSingle] = useAddSingleMutation();
	const [updateSingle] = useUpdateSingleMutation();
	const { uploadFile } = useFileUploader();
	const [audioMediaFile, setAudioMediaFile] = useState<FileInfo | null>(null);
	const [videoMediaFile, setVideoMediaFile] = useState<FileInfo | null>(null);
	const [iconFile, setIconFile] = useState<FileInfo | null>(null);
	const [introductionMediaVideoCoverFile, setIntroductionMediaVideoCoverFile] =
		useState<FileInfo | null>(null);
	const [introductionImageFile, setIntroductionImageFile] =
		useState<FileInfo | null>(null);
	const [introductionVideoFile, setIntroductionVideoFile] =
		useState<FileInfo | null>(null);
	const [informationImagesFile, setInformationImagesFile] = useState<
		FileInfo[] | null
	>(null);
	const { meditationId, meditationSingleId } = useParams();
	const imageInputRef = useRef<HTMLInputElement>(null);
	const imageInputRef2 = useRef<HTMLInputElement>(null);
	const imageInputRef3 = useRef<HTMLInputElement>(null);
	const imageInputRef4 = useRef<HTMLInputElement>(null);
	const imageInputRef5 = useRef<HTMLInputElement>(null);
	const imageInputRef6 = useRef<HTMLInputElement>(null);
	const imageInputRef7 = useRef<HTMLInputElement>(null);
	const showModal = useModal();

	const mediaType = [
		{ id: 1, name: t('meditation.audio') },
		{ id: 2, name: t('meditation.video') },
	];

	const introductionMediaType = [
		{ id: 3, name: t('meditation.image') },
		{ id: 2, name: t('meditation.video') },
	];

	const canList = [
		{ id: true, name: t('meditation.can') },
		{ id: false, name: t('meditation.cannot') },
	];

	const yesOrNoList = [
		{ id: 1, name: t('meditation.yes') },
		{ id: 0, name: t('meditation.no') },
	];
	const yesOrNoList2 = [
		{ id: true, name: t('meditation.yes') },
		{ id: false, name: t('meditation.no') },
	];

	const awardTypeList = [
		{ id: AwardType.Int, name: t('taskManage.Int') },
		{ id: AwardType.Prop, name: t('taskManage.Prop') },
		{ id: AwardType.IntProp, name: t('taskManage.IntProp') },
	];

	const { data: tagList } = useGetTagListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const transformResponse = useCallback(
		(response: ExerciseTagDTO[], type: string) => {
			if (response == null) return [];
			const newResponse: any[] = [];
			response.forEach((item) => {
				newResponse.push({
					id: item.tagId,
					name: (item as { [key: string]: any })[type],
				});
			});
			return newResponse;
		},
		[]
	);

	const defaultValues: DefaultValues<AddSingleRequest> = {
		mediaType: details?.mediaType || 1,
		mediaIntroductionTime: details?.mediaIntroductionTime || '',
		canCountTime: true,
		canCollectPoints: true,
		hasNew: true,
		hasReward: -1,
		rewardType: -1,
	};
	useEffect(() => {
		if (details) {
			if (details?.titleEn) methods.setValue('titleEn', details.titleEn);
			if (details?.titleZh) methods.setValue('titleZh', details.titleZh);
			if (details?.descriptionZh)
				methods.setValue('descriptionZh', details.descriptionZh);
			if (details?.descriptionEn)
				methods.setValue('descriptionEn', details.descriptionEn);
			if (details?.tags)
				methods.setValue(
					'tagList',
					(details.tags || []).map((item) => item.tagId)
				);
			if (details?.mediaType == 1) {
				setAudioMediaFile(details.media);
				methods.setValue('audioMediaFileName', details.media?.name);
			}
			if (details?.mediaType == 2) {
				setVideoMediaFile(details.media);
				methods.setValue('videoMediaFileName', details.media?.name);
			}

			if (details?.canCountTime != undefined || details?.canCountTime != null)
				methods.setValue('canCountTime', details?.canCountTime);
			if (
				details?.canCollectPoints != undefined ||
				details?.canCollectPoints != null
			)
				methods.setValue('canCollectPoints', details?.canCollectPoints);
			if (details?.hasNew != undefined || details?.hasNew != null)
				methods.setValue('hasNew', details?.hasNew);

			if (details?.reward) methods.setValue('reward', details.reward);
			if (details?.finishSummaryZh)
				methods.setValue('finishSummaryZh', details.finishSummaryZh);
			if (details?.finishSummaryEn)
				methods.setValue('finishSummaryEn', details.finishSummaryEn);
			if (details?.icon) setIconFile(details.icon);
			if (details?.hasIntroduction) {
				methods.setValue('hasIntroduction', details.hasIntroduction);
				if (details?.introductionContentEn)
					methods.setValue(
						'introductionContentEn',
						details.introductionContentEn
					);
				if (details?.introductionContentZh)
					methods.setValue(
						'introductionContentZh',
						details.introductionContentZh
					);
				if (details?.introductionMediaType)
					methods.setValue(
						'introductionMediaType',
						details.introductionMediaType
					);
				if (details?.introductionMediaType == 3)
					setIntroductionImageFile(details.introductionMediaImage);
				if (details?.introductionMediaType == 2) {
					setIntroductionVideoFile(details.introductionMediaVideo);
					methods.setValue(
						'introductionVideoFileName',
						details.introductionMediaVideo?.name
					);
					setIntroductionMediaVideoCoverFile(
						details.introductionMediaVideoCover
					);
					methods.setValue(
						'mediaVideoCoverFormField',
						details.introductionMediaVideoCover?.name
					);
				}
			}
			if (details?.hasInformation) {
				methods.setValue('hasInformation', details.hasInformation);

				if (details?.informationContentEn)
					methods.setValue(
						'informationContentEn',
						details.informationContentEn
					);
				if (details?.informationContentZh)
					methods.setValue(
						'informationContentZh',
						details.informationContentZh
					);
				if (details?.informationMediaImages)
					setInformationImagesFile(details.informationMediaImages);
			}

			if (Number(details?.rewardType) > -1)
				methods.setValue('rewardType', details.rewardType);
			if (details?.hasReward) {
				methods.setValue('hasReward', 1);
			} else {
				methods.setValue('hasReward', 0);
			}

			if (Number(details?.rewardPoints) > -1)
				methods.setValue('rewardPoints', details?.rewardPoints);
			if (details?.rewardList) {
				const arr = details.rewardList.map((item: any) => {
					return {
						...item,
						titleZh: item.seriesTitleZh + ' - ' + item.titleZh,
					};
				});
				methods.setValue('rewardList', arr);

				optionsReplace(arr);
			}
		}
	}, [details]);

	const formSchema = Yup.object().shape({
		titleZh: Yup.string().required(t('errorMessage.required')),
		canCollectPoints: Yup.boolean().required(t('errorMessage.pleaseSelect')),
		canCountTime: Yup.boolean().required(t('errorMessage.pleaseSelect')),
		hasNew: Yup.boolean().required(t('errorMessage.pleaseSelect')),
		mediaType: Yup.number().required(t('errorMessage.pleaseSelect')),
		mediaVideoCoverFormField: Yup.string()
			.nullable()
			.when('introductionVideoFileName', {
				is: (introductionVideoFileName: string) => {
					return introductionVideoFileName;
				},
				then: (schema) => schema.required(t('errorMessage.required')),
			}),
		hasReward: Yup.string().required(t('errorMessage.pleaseSelect')),
		audioMediaFileName: Yup.string()
			.nullable()
			.when('mediaType', {
				is: (mediaType: number) => mediaType == 1,
				then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			}),
		videoMediaFileName: Yup.string()
			.nullable()
			.when('mediaType', {
				is: (mediaType: number) => mediaType == 2,
				then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			}),
		rewardPoints: Yup.string()
			.nullable()
			.when(['rewardType', 'hasReward'], {
				is: (rewardType: string | number, hasReward: string | number) =>
					Number(hasReward) > 0 &&
					(rewardType == AwardType.Int || rewardType == AwardType.IntProp),
				then: (schema) => schema.required(t('errorMessage.required')),
			}),
		rewardType: Yup.string()
			.nullable()
			.when(['hasReward'], {
				is: (hasReward: string | number) => !!Number(hasReward),
				then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			}),
		rewardList: Yup.array()
			.nullable()
			.when(['rewardType', 'hasReward'], {
				is: (rewardType: string | number, hasReward: string | number) =>
					Number(hasReward) > 0 &&
					(rewardType == AwardType.Prop || rewardType == AwardType.IntProp),
				then: (schema) =>
					schema
						.required(t('errorMessage.pleaseSelect'))
						.min(1, t('errorMessage.pleaseSelect')),
				otherwise: (schema) => schema.nullable().notRequired(),
			}),
	});
	const methods = useForm<AddSingleRequest>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const mediaVideoCoverField = useController({
		name: 'mediaVideoCoverFormField',
		control: methods.control,
	});
	const audioMediaFileName = useController({
		name: 'audioMediaFileName',
		control: methods.control,
	});
	const videoMediaFileName = useController({
		name: 'videoMediaFileName',
		control: methods.control,
	});

	const {
		fields: rewardListField,
		append: optionsAppend,
		remove: optionsRemove,
		update: optionsUpdate,
		replace: optionsReplace,
	}: any = useFieldArray({
		control: methods.control,
		name: 'rewardList',
	});

	const handleGetRewardList = (list: any[]) => {
		if (list.length > 0) {
			const arr = methods.getValues('rewardList') || [];
			arr.push(list[0]);
			optionsReplace(arr);
		}
	};

	const handleDeleteReward = (index: number) => {
		optionsRemove(index);
	};

	const watchMediaType = methods.watch('mediaType');
	const watchHasContent = methods.watch('hasIntroduction');
	const watchIntroductionMediaType = methods.watch('introductionMediaType');
	const watchHasInformation = methods.watch('hasInformation');
	const watchRewardType = methods.watch('rewardType');
	const watchHasReward = methods.watch('hasReward');

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<AddSingleRequest> = async (data) => {
		console.log(data);
		console.log(details);
		const obj: AddSingleRequest = { ...data, tags: [], seriesId: meditationId };
		if (data.tagList) {
			data.tagList.map((item: string) => {
				const tag = tagList?.data.find((i) => i.tagId == item);
				obj.tags?.push(tag);
			});
		}
		if (data.mediaType == 1) obj.media = audioMediaFile;
		if (data.mediaType == 2) obj.media = videoMediaFile;
		if (data.hasReward > 0) {
			obj.hasReward = true;
			obj.rewardList = methods.getValues('rewardList')?.map((item) => {
				delete item.id;
				return {
					...item,
				};
			});
			if (data.rewardType == AwardType.Int) {
				delete obj.rewardList;
			}
			if (data.rewardType == AwardType.Prop) {
				delete obj.rewardPoints;
			}
		} else {
			obj.hasReward = false;
		}
		if (iconFile) obj.icon = iconFile;
		if (data.hasIntroduction) {
			obj.introductionContentZh = data.introductionContentZh;
			obj.introductionContentEn = data.introductionContentEn;
			obj.introductionMediaType = data.introductionMediaType;
			if (data.introductionMediaType == 3) {
				obj.introductionMediaImage = introductionImageFile;
			}
			if (data.introductionMediaType == 2) {
				obj.introductionMediaVideo = introductionVideoFile;
				obj.introductionMediaVideoCover = introductionMediaVideoCoverFile;
			} else {
				delete obj.introductionMediaVideoCover;
			}
		} else {
			obj.hasIntroduction = false;
			delete obj.introductionContentZh;
			delete obj.introductionContentEn;
			delete obj.introductionMediaImage;
			delete obj.introductionMediaVideo;
			delete obj.introductionMediaType;
			delete obj.introductionMediaVideoCover;
		}
		if (data.hasInformation) {
			obj.informationContentZh = data.informationContentZh;
			obj.informationContentEn = data.informationContentEn;
			obj.informationMediaImages = informationImagesFile;
		} else {
			obj.hasInformation = false;
			delete obj.informationContentZh;
			delete obj.informationContentEn;
			delete obj.informationMediaImages;
		}

		async function callApi() {
			const response =
				type == FormType.create
					? await addSingle(obj)
					: await updateSingle({
							...obj,
							id: meditationSingleId || '',
							seriesId: meditationId,
					  });
			if ('data' in response) {
				enqueueSnackbar(
					type == FormType.create
						? t('snackbar.createSingleSuccess')
						: t('snackbar.editSingleSuccess'),
					{
						variant: 'success',
						anchorOrigin: { horizontal: 'center', vertical: 'top' },
						autoHideDuration: 3000,
						action: (key) => {
							return <CloseIcon onClick={() => closeSnackbar(key)} />;
						},
					}
				);
				if (type == FormType.create) {
					navigate(`/exercise-manage/meditation-list/info/${meditationId}`);
				} else {
					setType(FormType.view);
				}
			}
		}

		if (type == FormType.edit && data.hasNew != details?.hasNew) {
			showModal((props: any) => (
				<ConfirmationDialog
					{...props}
					showCloseIcon={true}
					title={t('meditation.setNewExercise')}
					titleProps={{ variant: 'h6' }}
					description={
						<Typography variant='body1'>
							{t('meditation.setNewExerciseTips')}
						</Typography>
					}
					cancellationText={t('button.cancel')}
					confirmationText={t('button.isConfirm')}
					confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
					onConfirm={async () => {
						callApi();
					}}
				/>
			));
		} else {
			callApi();
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate(`/exercise-manage/meditation-list/info/${meditationId}`);
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleDeleteFile = (type: string, index?: number) => {
		if (type == 'audioMedia') {
			setAudioMediaFile(null);
			methods.setValue('audioMediaFileName', '');
			imageInputRef.current && (imageInputRef.current.value = '');
		} else if (type == 'videoMedia') {
			setVideoMediaFile(null);
			methods.setValue('videoMediaFileName', '');
			imageInputRef2.current && (imageInputRef2.current.value = '');
		} else if (type == 'icon') {
			setIconFile(null);
			imageInputRef3.current && (imageInputRef3.current.value = '');
		} else if (type == 'introductionImage') {
			setIntroductionImageFile(null);
			imageInputRef4.current && (imageInputRef4.current.value = '');
		} else if (type == 'introductionVideo') {
			setIntroductionVideoFile(null);
			methods.setValue('introductionVideoFileName', '');
			imageInputRef5.current && (imageInputRef5.current.value = '');
		} else if (type == 'informationImages') {
			const arr = (informationImagesFile || []).filter((_, i) => i !== index);
			setInformationImagesFile(arr);
			imageInputRef6.current && (imageInputRef6.current.value = '');
		} else if (type == 'preview') {
			setIntroductionMediaVideoCoverFile(null);
			imageInputRef7.current && (imageInputRef7.current.value = '');
			methods.setValue('mediaVideoCoverFormField', '');
		}
	};

	const handleUpload = async (
		event: React.ChangeEvent<HTMLInputElement>,
		type: string
	) => {
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;
		const res = await uploadFile(selectedFile);
		if (res) {
			if (type == 'audioMedia') {
				setAudioMediaFile(res);
				methods.setValue('audioMediaFileName', res.name);
				methods.trigger('audioMediaFileName');
			} else if (type == 'videoMedia') {
				setVideoMediaFile(res);
				methods.setValue('videoMediaFileName', res.name);
				methods.trigger('videoMediaFileName');
			} else if (type == 'icon') {
				setIconFile(res);
			} else if (type == 'introductionImage') {
				setIntroductionImageFile(res);
			} else if (type == 'introductionVideo') {
				setIntroductionVideoFile(res);
				methods.setValue('introductionVideoFileName', res.name);
			} else if (type == 'informationImages') {
				const arr = [...(informationImagesFile || []), res];
				setInformationImagesFile(arr);
				imageInputRef6.current && (imageInputRef6.current.value = '');
			} else if (type == 'preview') {
				setIntroductionMediaVideoCoverFile(res);
				imageInputRef7.current && (imageInputRef7.current.value = '');
				methods.setValue('mediaVideoCoverFormField', res.name);
			}
		}
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>

						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='titleZh'
											label={t('meditation.exerciseTitle')}
										/>
									</Grid>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='descriptionZh'
											label={t('meditation.description_')}
										/>
									</Grid>
									<Grid item xs={6}>
										<AppMultipleDropDownMenu
											control={methods.control}
											name='tagList'
											label={t('meditation.exerciseTag_')}
											options={transformResponse(
												tagList?.data || [],
												'tagNameZh'
											)}
										/>
									</Grid>
								</>
							)}
							{currentTab == 'en' && (
								<>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='titleEn'
											label={t('meditation.exerciseTitle_')}
											maxLength={100}
										/>
									</Grid>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='descriptionEn'
											label={t('meditation.description_')}
											maxLength={100}
										/>
									</Grid>
									<Grid item xs={6}>
										<AppMultipleDropDownMenu
											control={methods.control}
											name='tagList'
											label={t('meditation.exerciseTag_')}
											options={transformResponse(
												tagList?.data || [],
												'tagNameEn'
											)}
										/>
									</Grid>
								</>
							)}
							<Grid item xs={6}>
								<AppTextField
									control={methods.control}
									name='mediaIntroductionTime'
									label={t('meditation.second_')}
									inputType={InputType.number}
								/>
							</Grid>
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('meditation.exerciseType')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='mediaType'
									row
									options={mediaType}
								/>
							</Grid>
							{watchMediaType == 1 && (
								<Grid item xs={6}>
									<AppTextField
										disabled
										control={methods.control}
										name='audioMediaFileName'
										label={t('meditation.exerciseAudio')}
										InputProps={{
											endAdornment: (
												<InputAdornment position='end'>
													{audioMediaFile?.url && (
														<IconButton
															type='button'
															onClick={() => handleDeleteFile('audioMedia')}
														>
															<CloseIcon />
														</IconButton>
													)}

													<IconButton component='label'>
														<AttachFileIcon />
														<VisuallyHiddenInput
															type='file'
															accept='.mp3'
															onChange={(event) =>
																handleUpload(event, 'audioMedia')
															}
															ref={imageInputRef}
														/>
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
								</Grid>
							)}
							{watchMediaType == 2 && (
								<Grid item xs={6}>
									<AppTextField
										disabled
										control={methods.control}
										name='videoMediaFileName'
										label={t('meditation.exerciseVideo')}
										InputProps={{
											endAdornment: (
												<InputAdornment position='end'>
													{videoMediaFile?.url && (
														<IconButton
															type='button'
															onClick={() => handleDeleteFile('videoMedia')}
														>
															<CloseIcon />
														</IconButton>
													)}

													<IconButton component='label'>
														<AttachFileIcon />
														<VisuallyHiddenInput
															type='file'
															accept='.mp4'
															onChange={(event) =>
																handleUpload(event, 'videoMedia')
															}
															ref={imageInputRef2}
														/>
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
								</Grid>
							)}
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('meditation.isNumberTotal')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='canCountTime'
									row
									options={canList}
								/>
							</Grid>

							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('meditation.isNewExercise')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='hasNew'
									row
									options={yesOrNoList2}
								/>
							</Grid>

							<Grid item xs={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('meditation.exerciseCoverImage_')}
								</Typography>
								{iconFile && (
									<img
										src={iconFile?.url || ''}
										width={107}
										height={131}
										style={{
											objectFit: 'contain',
										}}
									/>
								)}

								<Box display={'flex'} alignItems={'center'}>
									{iconFile ? (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<LoopOutlinedIcon />}
										>
											{t('information.changeImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef3}
												onChange={(event) => {
													handleUpload(event, 'icon');
												}}
											/>
										</Button>
									) : (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<DriveFolderUploadOutlinedIcon />}
										>
											{t('information.uploadImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef3}
												onChange={(event) => {
													handleUpload(event, 'icon');
												}}
											/>
										</Button>
									)}
									{iconFile && (
										<Button
											component='label'
											sx={{ height: 24, marginLeft: '24px' }}
											color='error'
											endIcon={<DeleteOutlinedIcon />}
											onClick={() => {
												handleDeleteFile('icon');
											}}
										>
											{t('information.deleteImage')}
										</Button>
									)}
								</Box>
							</Grid>
						</Grid>
						<Divider />
						<Typography variant='h6' sx={{ paddingTop: '24px' }}>
							{t('common.completeExercise')}
						</Typography>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('meditation.isGetAward')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='canCollectPoints'
									row
									options={canList}
								/>
							</Grid>
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('meditation.finishedGetAward')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='hasReward'
									row
									options={yesOrNoList}
								/>
							</Grid>
							{Number(watchHasReward) ? (
								<>
									<Grid item xs={6}>
										<Typography variant='body2' color='common.neutral40'>
											{t('taskManage.awardType')}
										</Typography>
										<AppRadioGroup
											control={methods.control}
											name='rewardType'
											row
											options={awardTypeList}
										/>
									</Grid>
									{[AwardType.Int, AwardType.IntProp].includes(
										Number(watchRewardType)
									) && (
										<Grid item xs={6}>
											<AppTextField
												control={methods.control}
												name='rewardPoints'
												label={t('taskManage.rewardPoints')}
												inputType={InputType.number}
												maxLength={5}
											/>
										</Grid>
									)}
									{[AwardType.Prop, AwardType.IntProp].includes(
										Number(watchRewardType)
									) &&
										rewardListField.map((item: any, index: number) => (
											<React.Fragment key={item.rewardId}>
												{index == 0 && watchRewardType == AwardType.Prop && (
													<Grid item xs={6}></Grid>
												)}
												<Grid item xs={6}>
													<AppTextField
														disabled
														control={methods.control}
														name={`rewardList.${index}.titleZh`}
														label={t('regularTask.rewardItem')}
													/>
												</Grid>
												<Grid item xs={6} sx={{ display: 'flex' }}>
													<AppTextField
														control={methods.control}
														name={`rewardList.${index}.rewardNum`}
														label={t('regularTask.rewardItemCount')}
														inputType={InputType.number}
													/>
													<IconButton
														aria-label='close'
														onClick={() => handleDeleteReward(index)}
														color='error'
													>
														<DeleteOutlineOutlinedIcon />
													</IconButton>
												</Grid>
											</React.Fragment>
										))}
									{[AwardType.Prop, AwardType.IntProp].includes(
										Number(watchRewardType)
									) && (
										<Grid item xs={12}>
											<AddRewardItemDialog
												handleGetRewardList={handleGetRewardList}
											/>
											<FormHelperText style={{ color: '#d32f2f' }}>
												{typeof methods.formState.errors.rewardList?.message ===
												'string'
													? methods.formState.errors.rewardList.message
													: ''}
											</FormHelperText>
										</Grid>
									)}
								</>
							) : (
								''
							)}

							{currentTab == 'zh' && (
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='finishSummaryZh'
										multiline
										label={t('common.finishSummary_')}
										maxLength={500}
										minRows={4}
									/>
								</Grid>
							)}
							{currentTab == 'en' && (
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='finishSummaryEn'
										multiline
										label={t('common.finishSummary_')}
										maxLength={500}
										minRows={4}
									/>
								</Grid>
							)}
						</Grid>
						<Divider />
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={12}>
								<SingleCheckbox
									control={methods.control}
									name='hasIntroduction'
									label={t('meditation.hasContent')}
								/>
							</Grid>
							{watchHasContent && currentTab == 'zh' && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('meditation.introductionContent')}
									</Typography>
									<AppRichTextEditor
										control={methods.control}
										name='introductionContentZh'
									/>
								</Grid>
							)}
							{watchHasContent && currentTab == 'en' && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('meditation.introductionContent')}
									</Typography>
									<AppRichTextEditor
										control={methods.control}
										name='introductionContentEn'
									/>
								</Grid>
							)}
							{watchHasContent && (
								<Grid item xs={6}>
									<Typography variant='body2' color='common.neutral40'>
										{t('meditation.introductionMediaType_')}
									</Typography>
									<AppRadioGroup
										control={methods.control}
										name='introductionMediaType'
										row
										options={introductionMediaType}
									/>
								</Grid>
							)}
							{watchHasContent && watchIntroductionMediaType == 3 && (
								<Grid item xs={6}>
									<Typography variant='body2' color='common.neutral40'>
										{t('meditation.introductionMediaImage_')}
									</Typography>
									{introductionImageFile && (
										<img
											src={introductionImageFile?.url || ''}
											width={390}
											height={138}
											style={{
												objectFit: 'contain',
											}}
										/>
									)}

									<Box display={'flex'} alignItems={'center'}>
										{introductionImageFile ? (
											<Button
												component='label'
												sx={{ height: 24 }}
												endIcon={<LoopOutlinedIcon />}
											>
												{t('information.changeImage')}
												<VisuallyHiddenInput
													type='file'
													name='thumbnail'
													accept='image/*'
													ref={imageInputRef4}
													onChange={(event) => {
														handleUpload(event, 'introductionImage');
													}}
												/>
											</Button>
										) : (
											<Button
												component='label'
												sx={{ height: 24 }}
												endIcon={<DriveFolderUploadOutlinedIcon />}
											>
												{t('information.uploadImage')}
												<VisuallyHiddenInput
													type='file'
													name='thumbnail'
													accept='image/*'
													ref={imageInputRef4}
													onChange={(event) => {
														handleUpload(event, 'introductionImage');
													}}
												/>
											</Button>
										)}
										{introductionImageFile && (
											<Button
												component='label'
												sx={{ height: 24, marginLeft: '24px' }}
												color='error'
												endIcon={<DeleteOutlinedIcon />}
												onClick={() => {
													handleDeleteFile('introductionImage');
												}}
											>
												{t('information.deleteImage')}
											</Button>
										)}
									</Box>
								</Grid>
							)}
							{watchHasContent && watchIntroductionMediaType == 2 && (
								<>
									<Grid item xs={6}>
										<AppTextField
											disabled
											control={methods.control}
											name='introductionVideoFileName'
											label={t('meditation.introductionMediaVideo_')}
											InputProps={{
												endAdornment: (
													<InputAdornment position='end'>
														{introductionVideoFile?.url && (
															<IconButton
																type='button'
																onClick={() =>
																	handleDeleteFile('introductionVideo')
																}
															>
																<CloseIcon />
															</IconButton>
														)}

														<IconButton component='label'>
															<AttachFileIcon />
															<VisuallyHiddenInput
																type='file'
																accept='.mp4'
																onChange={(event) =>
																	handleUpload(event, 'introductionVideo')
																}
																ref={imageInputRef5}
															/>
														</IconButton>
													</InputAdornment>
												),
											}}
										/>
									</Grid>
									<Grid item xs={6}>
										<Typography variant='body2' color='common.neutral40'>
											{t('day21.previewImage')}
										</Typography>
										{introductionMediaVideoCoverFile && (
											<img
												src={introductionMediaVideoCoverFile?.url || ''}
												width={390}
												height={138}
												style={{
													objectFit: 'contain',
												}}
											/>
										)}

										<Box display={'flex'} alignItems={'center'}>
											{introductionMediaVideoCoverFile ? (
												<Button
													component='label'
													sx={{ height: 24 }}
													endIcon={<LoopOutlinedIcon />}
												>
													{t('information.changeImage')}
													<VisuallyHiddenInput
														type='file'
														name='thumbnail'
														accept='image/*'
														ref={imageInputRef7}
														onChange={(event) => {
															handleUpload(event, 'preview');
														}}
													/>
												</Button>
											) : (
												<Button
													component='label'
													sx={{ height: 24 }}
													endIcon={<DriveFolderUploadOutlinedIcon />}
												>
													{t('information.uploadImage')}
													<VisuallyHiddenInput
														type='file'
														name='thumbnail'
														accept='image/*'
														ref={imageInputRef7}
														onChange={(event) => {
															handleUpload(event, 'preview');
														}}
													/>
												</Button>
											)}
											{introductionMediaVideoCoverFile && (
												<Button
													component='label'
													sx={{ height: 24, marginLeft: '24px' }}
													color='error'
													endIcon={<DeleteOutlinedIcon />}
													onClick={() => {
														handleDeleteFile('preview');
													}}
												>
													{t('information.deleteImage')}
												</Button>
											)}
										</Box>
										{methods.getValues('introductionVideoFileName') &&
											!methods.getValues('mediaVideoCoverFormField') && (
												<FormHelperText
													color='error'
													style={{ color: '#d32f2f' }}
												>
													{mediaVideoCoverField.fieldState.error?.message}
												</FormHelperText>
											)}
									</Grid>
								</>
							)}
						</Grid>
						<Divider />
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={12}>
								<SingleCheckbox
									control={methods.control}
									name='hasInformation'
									label={t('meditation.hasInformation')}
								/>
							</Grid>
							{watchHasInformation && currentTab == 'zh' && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('meditation.informationContent')}
									</Typography>
									<AppRichTextEditor
										control={methods.control}
										name='informationContentZh'
									/>
								</Grid>
							)}
							{watchHasInformation && currentTab == 'en' && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('meditation.informationContent')}
									</Typography>
									<AppRichTextEditor
										control={methods.control}
										name='informationContentEn'
									/>
								</Grid>
							)}
							{watchHasInformation && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('meditation.informationImage_')}
									</Typography>

									<Box display={'flex'} alignItems={'center'} flexWrap={'wrap'}>
										{informationImagesFile?.map((item, index) => (
											<Box
												key={item.fileKey}
												display={'flex'}
												alignItems={'center'}
												flexDirection={'column'}
												sx={{ marginRight: '50px' }}
											>
												<img
													src={item.url || ''}
													width={110}
													height={124}
													style={{
														objectFit: 'contain',
													}}
												/>
												<Button
													component='label'
													sx={{ height: 24, margin: '0 auto' }}
													color='error'
													endIcon={<DeleteOutlinedIcon />}
													onClick={() => {
														handleDeleteFile('informationImages', index);
													}}
												>
													{t('information.deleteImage')}
												</Button>
											</Box>
										))}

										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<DriveFolderUploadOutlinedIcon />}
										>
											{t('information.uploadImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef6}
												onChange={(event) => {
													handleUpload(event, 'informationImages');
												}}
											/>
										</Button>
									</Box>
								</Grid>
							)}
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.create')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
